import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, Heart } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3f20d01d-d5e0-46a1-b467-01c97ab78497/images/34aef0f4f2d203524f380b2f402cf5c036078693893a4b4b231659e194f3e570.png";

export default function Hero() {
  const { t } = useT();
  const [videoReady, setVideoReady] = useState(false);
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative pt-32 sm:pt-40 pb-24 sm:pb-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT — TEXT */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-2 mb-7">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#EDEDEE] border border-[#BCB3A6]/40 text-xs uppercase tracking-[0.22em] text-[#3F5043]">
                <Heart size={12} className="text-[#875F45]" />
                {t("hero.badge")}
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] tracking-tight text-[#3F5043]"
            >
              {t("hero.title1")} <br />
              {t("hero.title2")}{" "}
              <span className="italic text-[#875F45]">{t("hero.title3")}</span>
            </h1>

            <p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#3F5043]/75">
              {t("hero.subtitle")}
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                data-testid="hero-cta-book"
                href="/book"
                className="group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-medium bg-[#3F5043] text-[#F8F7F3] hover:bg-[#899480] transition-all duration-300"
              >
                {t("hero.cta.book")}
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <button
                data-testid="hero-cta-contact"
                onClick={() => scrollTo("#contact")}
                className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-medium border border-[#3F5043]/30 text-[#3F5043] hover:bg-[#3F5043] hover:text-[#F8F7F3] transition-all duration-300"
              >
                {t("hero.cta.contact")}
              </button>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.2em] text-[#899480] font-semibold">
              <div className="flex items-center gap-2">
                <MapPin size={13} className="text-[#875F45]" />
                {t("hero.tag.location")}
              </div>
              <div>{t("hero.tag.nurse")}</div>
              <div>{t("hero.tag.lang")}</div>
              <div>{t("hero.tag.format")}</div>
            </div>
          </motion.div>

          {/* RIGHT — IMAGE */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: "easeOut", delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-[#EDEDEE]">
              <img
                src={HERO_IMG}
                alt="Two people holding hands gently in warm sunlight — fertility support"
                className="w-full h-full object-cover"
                loading="eager"
              />
              <video
                data-testid="hero-video"
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                  videoReady ? "opacity-100" : "opacity-0"
                }`}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                onCanPlay={() => setVideoReady(true)}
              >
                <source src="/videos/hero-loop.mp4" type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-tr from-[#3F5043]/15 via-transparent to-transparent pointer-events-none" />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.6 }}
              className="absolute -bottom-6 -left-4 sm:-left-10 bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-2xl px-6 py-5 shadow-sm max-w-[260px]"
            >
              <div className="text-xs uppercase tracking-[0.22em] text-[#899480] font-semibold mb-2">
                {t("hero.quote.label")}
              </div>
              <p className="font-serif text-lg leading-snug text-[#3F5043]">
                {t("hero.quote.text")}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
